import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Grid,
  Alert,
  LinearProgress,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  SelectChangeEvent,
  FormControlLabel,
  TextField,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  PlayArrow,
  Settings,
  Timeline,
  CheckCircle,
  Error as ErrorIcon,
  Warning,
  DirectionsCar,
  LocationOn,
  Work,
  Speed,
  Assessment,
  ExpandMore,
  Stop,
  Refresh,
  BugReport,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { ProfessionalLayout } from '../components/layout/ProfessionalLayout';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useVRPData } from '../contexts/VRPDataContext';
import { useDatasetOptimizationData } from '../hooks/useDatasetData';
import { ScenarioDatasetBreadcrumbs } from '../components/navigation/ScenarioDatasetBreadcrumbs';
import type { Location } from '../types/vrp';
import { StoredOptimizationResults } from '../components/optimization/StoredOptimizationResults';
import { OptimizationDebugger } from '../components/optimization/OptimizationDebugger';
import { OptimizationHistory } from '../components/optimization/OptimizationHistory';
import { formatDuration } from '../utils/timeFormat';

// VROOM Optimization Types
interface VroomVehicle {
  id: number;
  start: [number, number];
  end: [number, number];
  capacity: number[];
  skills: number[];
  time_window: [number, number];
  costs: {
    fixed: number;
    per_hour: number;
    per_km: number;
  };
  max_distance?: number;
  max_travel_time?: number;
}

interface VroomJob {
  id: number;
  location: [number, number];
  service: number;
  delivery?: number[];
  pickup?: number[];
  skills: number[];
  priority: number;
  time_windows?: [number, number][];
}

interface VroomRequest {
  vehicles: VroomVehicle[];
  jobs: VroomJob[];
  options: {
    g: boolean;
    useMatrix: boolean;
    threads?: number;
  };
}

interface VroomResponse {
  code: number;
  summary: {
    cost: number;
    routes: number;
    unassigned: number;
    distance: number;
    duration: number;
  };
  routes: VroomRoute[];
  unassigned: VroomUnassigned[];
}

interface VroomRoute {
  vehicle: number;
  cost: number;
  distance: number;
  duration: number;
  steps: VroomStep[];
}

interface VroomStep {
  type: 'start' | 'job' | 'end';
  id?: number;
  location: [number, number];
  arrival: number;
  service?: number;
  distance: number;
  load?: number[];
}

interface VroomUnassigned {
  id: number;
  location: [number, number];
  reason: string;
}

interface OptimizationSettings {
  includeGeometry: boolean;
  useMatrix: boolean;
  threads: number;
  maxDistance: number;
  maxTravelTime: number;
  algorithm: 'vroom' | 'genetic' | 'simulated_annealing';
  timeWindowTolerance: number;
}

export const Optimization: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  // TODO: Replace with Toolpad navigation
  const currentProject = null;
  const { currentDataset, statistics, validateDataset } = useVRPData();
  
  // Dataset-specific data loading for optimization
  const {
    vehicles,
    jobs,
    locations,
    isLoading: isDatasetLoading,
    validation: dataValidation,
    isDataComplete,
    error: dataError,
    refetchAll: refetchDatasetData
  } = useDatasetOptimizationData(currentProject?.id || 0, {
    enabled: !!(currentScenario && currentDataset && currentProject?.id)
  });

  // State management
  const [optimizationState, setOptimizationState] = useState<'idle' | 'running' | 'completed' | 'error'>('idle');
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<VroomResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [logs, setLogs] = useState<Array<{ timestamp: string; message: string; type: 'info' | 'warning' | 'error' | 'success' }>>([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [healthStatus, setHealthStatus] = useState<'unknown' | 'checking' | 'healthy' | 'unhealthy'>('unknown');
  const [settings, setSettings] = useState<OptimizationSettings>({
    includeGeometry: true,
    useMatrix: false,
    threads: 4,
    maxDistance: 100000,
    maxTravelTime: 28800,
    algorithm: 'vroom',
    timeWindowTolerance: 300,
  });

  const isLoading = isDatasetLoading;

  // Add log entry
  const addLog = useCallback((message: string, type: 'info' | 'warning' | 'error' | 'success' = 'info') => {
    setLogs(prev => [...prev, {
      timestamp: new Date().toLocaleTimeString(),
      message,
      type
    }]);
  }, []);

  // Transform data for VROOM format
  const transformToVroomFormat = useCallback((): VroomRequest => {
    addLog('🔄 Starting data transformation to optimisation engine format...', 'info');
    // Defensive array handling for all data types
    const vehiclesArray = Array.isArray(vehicles) ? vehicles : (vehicles ? [vehicles] : []);
    const jobsArray = Array.isArray(jobs) ? jobs : (jobs ? [jobs] : []);
    const locationsArray = Array.isArray(locations) ? locations : (locations ? [locations] : []);
    
    addLog(`📊 Raw data: ${vehiclesArray.length} vehicles, ${jobsArray.length} jobs, ${locationsArray.length} locations`, 'info');

    // Find depot location with valid coordinates
    const depotLocation = locationsArray.find(loc => 
      loc.location_type === 'depot' && 
      loc.location_lon != null && 
      loc.location_lat != null &&
      !isNaN(loc.location_lon) && !isNaN(loc.location_lat)
    );
    
    // Use first location with valid coordinates as fallback
    const fallbackLocation = locationsArray.find(loc => 
      loc.location_lon != null && 
      loc.location_lat != null &&
      !isNaN(loc.location_lon) && !isNaN(loc.location_lat)
    );
    
    // Default coordinates (Johannesburg)
    const defaultCoords: [number, number] = [28.0473, -26.2041];
    
    const getLocationCoords = (location?: Location): [number, number] => {
      if (location?.location_lon != null && location?.location_lat != null &&
          !isNaN(location.location_lon) && !isNaN(location.location_lat)) {
        return [location.location_lon, location.location_lat];
      }
      return defaultCoords;
    };

    // Log depot information
    if (depotLocation) {
      addLog(`🏢 Found depot: ${depotLocation.name} at [${depotLocation.location_lon}, ${depotLocation.location_lat}]`, 'success');
    } else if (fallbackLocation) {
      addLog(`⚠️ No depot found, using fallback location: ${fallbackLocation.name}`, 'warning');
    } else {
      addLog(`⚠️ No valid locations found, using default coordinates: [${defaultCoords[0]}, ${defaultCoords[1]}]`, 'warning');
    }

    // Transform vehicles with detailed validation - Use defensive array from above
    const vroomVehicles: VroomVehicle[] = vehiclesArray.map((vehicle, index) => {
      // PRIORITY 1: Use vehicle's direct coordinates if available
      let startCoords: [number, number];
      if (vehicle.start_lon != null && vehicle.start_lat != null && 
          !isNaN(vehicle.start_lon) && !isNaN(vehicle.start_lat)) {
        startCoords = [vehicle.start_lon, vehicle.start_lat];
      } else {
        // PRIORITY 2: Use vehicle's referenced start location
        const vehicleStartLocation = locationsArray.find(loc => loc.id === vehicle.start_location_id);
        startCoords = getLocationCoords(vehicleStartLocation || depotLocation || fallbackLocation);
      }
      
      // CRITICAL: Ensure capacity dimensions match delivery/pickup dimensions
      // Use single dimension for consistency with jobs
      let capacity: number[];
      if (Array.isArray(vehicle.capacity)) {
        capacity = [Math.max(Number(vehicle.capacity[0]) || 1000, 1)]; // Take first dimension only
      } else {
        capacity = [Math.max(Number(vehicle.capacity) || 1000, 1)];
      }
      
      // Handle skills properly - convert skill IDs to 1-based indices if they're actual IDs
      let skillsArray: number[];
      if (vehicle.skills && Array.isArray(vehicle.skills) && vehicle.skills.length > 0) {
        // If skills contain actual skill IDs, use them directly (ensuring they're positive)
        skillsArray = (vehicle.skills || []).map(skill => Math.max(Number(skill) || 1, 1));
      } else {
        // Default skill set
        skillsArray = [1];
      }
      
      const vroomVehicle = {
        id: vehicle.id,
        start: startCoords,
        end: startCoords,
        capacity,
        skills: skillsArray,
        time_window: [0, 86400] as [number, number], // 24 hours
        costs: {
          fixed: Math.round(Number(vehicle.cost_fixed) || 100),
          per_hour: Math.round(Number(vehicle.cost_per_hour) || 3600),
          per_km: Math.round(Number(vehicle.cost_per_km) || 100),
        },
        max_distance: settings.maxDistance > 0 ? settings.maxDistance : undefined,
        max_travel_time: settings.maxTravelTime > 0 ? settings.maxTravelTime : undefined,
      };
      
      // Determine coordinate source for logging
      let coordinateSource: string;
      if (vehicle.start_lon != null && vehicle.start_lat != null && 
          !isNaN(vehicle.start_lon) && !isNaN(vehicle.start_lat)) {
        coordinateSource = 'direct_coordinates';
      } else {
        const vehicleStartLocation = locationsArray.find(loc => loc.id === vehicle.start_location_id);
        coordinateSource = vehicleStartLocation?.name || (depotLocation?.name || fallbackLocation?.name || 'default');
      }
      
      addLog(`🚗 Vehicle ${vehicle.id}: source="${coordinateSource}" coords=[${startCoords[0]}, ${startCoords[1]}], capacity=${JSON.stringify(capacity)}, skills=${JSON.stringify(skillsArray)}`, 'info');
      return vroomVehicle;
    });

    // Transform jobs with detailed validation - Use defensive array from above
    const vroomJobs: VroomJob[] = jobsArray.map((job) => {
      const jobLocation = locationsArray.find(loc => loc.id === job.location_id);
      const jobCoords = getLocationCoords(jobLocation);
      
      // Validate job coordinates
      if (jobCoords[0] === defaultCoords[0] && jobCoords[1] === defaultCoords[1] && !jobLocation) {
        addLog(`⚠️ Job ${job.id} has no valid location (location_id: ${job.location_id})`, 'warning');
      }
      
      // CRITICAL: Ensure consistent dimensions for delivery and pickup arrays
      // All jobs must have the same number of dimensions to avoid VROOM error
      const standardDimensions = 1; // Use single dimension for consistency
      
      // Ensure delivery requirements are valid (always present for VROOM)
      let delivery: number[];
      if (job.delivery && Array.isArray(job.delivery) && job.delivery.length > 0) {
        delivery = [Math.max(Number(job.delivery[0]) || 1, 1)]; // Take first dimension only
      } else if (job.delivery && Number(job.delivery) > 0) {
        delivery = [Math.max(Number(job.delivery), 1)];
      } else {
        delivery = [1]; // Minimum delivery requirement
      }
      
      // Handle pickup requirements (optional but must match delivery dimensions if present)
      let pickup: number[] | undefined;
      if (job.pickup && Array.isArray(job.pickup) && job.pickup.length > 0 && Number(job.pickup[0]) > 0) {
        pickup = [Math.max(Number(job.pickup[0]) || 0, 0)]; // Take first dimension only, same as delivery
      } else if (job.pickup && Number(job.pickup) > 0) {
        pickup = [Math.max(Number(job.pickup), 0)];
      }
      // If pickup is 0 or undefined, leave it undefined to avoid empty arrays
      
      // Handle skills properly - match vehicle skill handling
      let skillsArray: number[];
      if (job.skills && Array.isArray(job.skills) && job.skills.length > 0) {
        skillsArray = (job.skills || []).map(skill => Math.max(Number(skill) || 1, 1));
      } else {
        skillsArray = [1]; // Default skill requirement
      }
      
      // Service time in seconds (database already stores seconds)
      const serviceTime = Math.max(Number(job.service) || 300, 0);
      
      const vroomJob = {
        id: job.id,
        location: jobCoords,
        service: serviceTime,
        delivery,
        pickup,
        skills: skillsArray,
        priority: Math.max(Number(job.priority) || 100, 1),
        time_windows: job.time_windows && Array.isArray(job.time_windows) && job.time_windows.length > 0 ? 
          (job.time_windows || []).map(tw => [
            Math.max(Number(tw.start) || 0, 0),
            Math.max(Number(tw.end) || 86400, Number(tw.start) || 0)
          ] as [number, number]) : undefined,
      };
      
      addLog(`💼 Job ${job.id}: coords=[${jobCoords[0]}, ${jobCoords[1]}], delivery=${JSON.stringify(delivery)}, skills=${JSON.stringify(skillsArray)}, service=${serviceTime}s`, 'info');
      return vroomJob;
    });

    // Validation checks
    const validationIssues: string[] = [];
    
    // Check vehicle capacities vs job requirements
    const totalVehicleCapacity = vroomVehicles.reduce((sum, v) => sum + v.capacity[0], 0);
    const totalJobDemand = vroomJobs.reduce((sum, j) => sum + (j.delivery?.[0] || 0), 0);
    
    if (totalJobDemand > totalVehicleCapacity) {
      validationIssues.push(`Total job demand (${totalJobDemand}) exceeds total vehicle capacity (${totalVehicleCapacity})`);
    }
    
    // Check skill compatibility
    const vehicleSkills = new Set(vroomVehicles.flatMap(v => v.skills));
    const requiredSkills = new Set(vroomJobs.flatMap(j => j.skills));
    const missingSkills = [...requiredSkills].filter(skill => !vehicleSkills.has(skill));
    
    if (missingSkills.length > 0) {
      validationIssues.push(`Jobs require skills ${JSON.stringify(missingSkills)} that no vehicles have`);
    }
    
    // Log validation results
    if (validationIssues.length > 0) {
      validationIssues.forEach(issue => addLog(`❌ VALIDATION ISSUE: ${issue}`, 'error'));
    } else {
      addLog('✅ All validation checks passed', 'success');
    }
    
    addLog(`📦 Final optimisation request: ${vroomVehicles.length} vehicles, ${vroomJobs.length} jobs`, 'success');
    addLog(`🎯 Skills summary - Vehicle skills: ${JSON.stringify([...vehicleSkills])}, Job skills: ${JSON.stringify([...requiredSkills])}`, 'info');

    return {
      vehicles: vroomVehicles,
      jobs: vroomJobs,
      options: {
        g: settings.includeGeometry,
        useMatrix: settings.useMatrix,
        threads: settings.threads,
      },
    };
  }, [vehicles, jobs, locations, settings, addLog]);

  // Data diagnostics function
  const runDataDiagnostics = useCallback(() => {
    addLog('🔍 Running comprehensive data diagnostics...', 'info');
    
    // Defensive array handling for diagnostics
    const vehiclesArray = Array.isArray(vehicles) ? vehicles : (vehicles ? [vehicles] : []);
    const jobsArray = Array.isArray(jobs) ? jobs : (jobs ? [jobs] : []);
    const locationsArray = Array.isArray(locations) ? locations : (locations ? [locations] : []);
    
    // Analyze vehicles
    addLog(`🚗 Vehicle Analysis:`, 'info');
    vehiclesArray.forEach((vehicle, index) => {
      const capacity = Array.isArray(vehicle.capacity) ? vehicle.capacity : [vehicle.capacity || 1000];
      const totalCapacity = capacity.reduce((sum, cap) => sum + (cap || 0), 0);
      const skills = vehicle.skills || [];
      
      addLog(`  Vehicle ${vehicle.id}: capacity=${totalCapacity}, skills=[${skills.join(',')}]`, 'info');
    });
    
    // Analyze jobs
    addLog(`💼 Job Analysis:`, 'info');
    let totalDemand = 0;
    jobsArray.forEach((job, index) => {
      const jobLocation = locationsArray.find(loc => loc.id === job.location_id);
      const demand = job.delivery || 100;
      totalDemand += demand;
      const coords = jobLocation ? `[${jobLocation.location_lon}, ${jobLocation.location_lat}]` : 'MISSING';
      const skills = job.skills || [];
      
      addLog(`  Job ${job.id}: demand=${demand}, skills=[${skills.join(',')}], location=${coords}`, 'info');
    });
    
    // Analyze locations
    addLog(`📍 Location Analysis:`, 'info');
    const validLocations = locationsArray.filter(loc => 
      loc.location_lon != null && loc.location_lat != null &&
      !isNaN(loc.location_lon) && !isNaN(loc.location_lat)
    );
    addLog(`  Valid locations: ${validLocations.length}/${locationsArray.length}`, validLocations.length === locationsArray.length ? 'success' : 'warning');
    
    const depotCount = locationsArray.filter(loc => loc.location_type === 'depot').length;
    addLog(`  Depot locations: ${depotCount}`, depotCount > 0 ? 'success' : 'warning');
    
    // Capacity vs demand analysis
    const totalVehicleCapacity = vehiclesArray.reduce((sum, vehicle) => {
      const capacity = Array.isArray(vehicle.capacity) ? vehicle.capacity : [vehicle.capacity || 1000];
      return sum + capacity.reduce((capSum, cap) => capSum + (cap || 0), 0);
    }, 0);
    
    addLog(`📊 Capacity Analysis:`, 'info');
    addLog(`  Total vehicle capacity: ${totalVehicleCapacity}`, 'info');
    addLog(`  Total job demand: ${totalDemand}`, 'info');
    addLog(`  Capacity ratio: ${(totalVehicleCapacity / totalDemand * 100).toFixed(1)}%`, 
           totalVehicleCapacity >= totalDemand ? 'success' : 'error');
    
    // Skills analysis
    const vehicleSkills = new Set(vehiclesArray.flatMap(v => v.skills || []));
    const jobSkills = new Set(jobsArray.flatMap(j => j.skills || []));
    const skillsOverlap = [...jobSkills].filter(skill => vehicleSkills.has(skill));
    
    addLog(`🎯 Skills Analysis:`, 'info');
    addLog(`  Vehicle skills: [${[...vehicleSkills].join(',')}]`, 'info');
    addLog(`  Job skills: [${[...jobSkills].join(',')}]`, 'info');
    addLog(`  Skills overlap: [${skillsOverlap.join(',')}]`, 
           skillsOverlap.length > 0 ? 'success' : 'warning');

  }, [vehicles, jobs, locations, addLog]);

  // Comprehensive pre-optimization validation
  const validateOptimizationData = useCallback((request: VroomRequest): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];
    
    addLog('🔍 Running pre-optimization validation...', 'info');
    
    // 1. Basic data presence validation
    if (!request.vehicles || !Array.isArray(request.vehicles) || request.vehicles.length === 0) {
      errors.push('No vehicles available for optimization');
    }
    
    if (!request.jobs || !Array.isArray(request.jobs) || request.jobs.length === 0) {
      errors.push('No jobs available for optimization');
    }
    
    // 2. Vehicle validation
    let maxCapacityDimensions = 0;
    (request.vehicles || []).forEach((vehicle, index) => {
      // Required fields for vehicles
      if (!vehicle.id || vehicle.id <= 0) {
        errors.push(`Vehicle ${index}: Invalid or missing ID`);
      }
      
      if (!vehicle.start || vehicle.start.length !== 2 || 
          vehicle.start.some(coord => isNaN(coord) || coord < -180 || coord > 180)) {
        errors.push(`Vehicle ${vehicle.id}: Invalid start coordinates`);
      }
      
      if (!vehicle.end || vehicle.end.length !== 2 || 
          vehicle.end.some(coord => isNaN(coord) || coord < -180 || coord > 180)) {
        errors.push(`Vehicle ${vehicle.id}: Invalid end coordinates`);
      }
      
      // Capacity validation (required)
      if (!vehicle.capacity || !Array.isArray(vehicle.capacity) || vehicle.capacity.length === 0) {
        errors.push(`Vehicle ${vehicle.id}: Missing or invalid capacity array`);
      } else {
        maxCapacityDimensions = Math.max(maxCapacityDimensions, vehicle.capacity.length);
        if (vehicle.capacity.some(cap => isNaN(cap) || cap < 0)) {
          errors.push(`Vehicle ${vehicle.id}: Invalid capacity values (must be positive numbers)`);
        }
      }
      
      // Skills validation (optional but must be array if present)
      if (vehicle.skills && (!Array.isArray(vehicle.skills) || vehicle.skills.some(skill => isNaN(skill) || skill <= 0))) {
        errors.push(`Vehicle ${vehicle.id}: Invalid skills array (must be positive integers)`);
      }
      
      // Time window validation (required)
      if (!vehicle.time_window || !Array.isArray(vehicle.time_window) || vehicle.time_window.length !== 2) {
        errors.push(`Vehicle ${vehicle.id}: Invalid time window (must be [start, end])`);
      } else if (vehicle.time_window[0] >= vehicle.time_window[1]) {
        errors.push(`Vehicle ${vehicle.id}: Invalid time window (start must be before end)`);
      }
      
      // Costs validation (required)
      if (!vehicle.costs || typeof vehicle.costs !== 'object') {
        errors.push(`Vehicle ${vehicle.id}: Missing costs object`);
      } else {
        const { fixed, per_hour, per_km } = vehicle.costs;
        if (isNaN(fixed) || fixed < 0 || isNaN(per_hour) || per_hour < 0 || isNaN(per_km) || per_km < 0) {
          errors.push(`Vehicle ${vehicle.id}: Invalid cost values (must be positive numbers)`);
        }
      }
    });
    
    // 3. Job validation
    let maxDeliveryDimensions = 0;
    let maxPickupDimensions = 0;
    let hasPickups = false;
    let hasDeliveries = false;
    
    (request.jobs || []).forEach((job, index) => {
      // Required fields for jobs
      if (!job.id || job.id <= 0) {
        errors.push(`Job ${index}: Invalid or missing ID`);
      }
      
      if (!job.location || job.location.length !== 2 || 
          job.location.some(coord => isNaN(coord) || coord < -180 || coord > 180)) {
        errors.push(`Job ${job.id}: Invalid location coordinates`);
      }
      
      // Service time validation (required, must be non-negative)
      if (isNaN(job.service) || job.service < 0) {
        errors.push(`Job ${job.id}: Invalid service time (must be non-negative number)`);
      }
      
      // Priority validation (required, must be positive)
      if (isNaN(job.priority) || job.priority <= 0) {
        errors.push(`Job ${job.id}: Invalid priority (must be positive number)`);
      }
      
      // Delivery validation (optional but must be consistent if present)
      if (job.delivery) {
        if (!Array.isArray(job.delivery) || job.delivery.some(d => isNaN(d) || d < 0)) {
          errors.push(`Job ${job.id}: Invalid delivery array (must be positive numbers)`);
        } else {
          maxDeliveryDimensions = Math.max(maxDeliveryDimensions, job.delivery.length);
          hasDeliveries = true;
        }
      }
      
      // Pickup validation (optional but must be consistent if present)
      if (job.pickup) {
        if (!Array.isArray(job.pickup) || job.pickup.some(p => isNaN(p) || p < 0)) {
          errors.push(`Job ${job.id}: Invalid pickup array (must be positive numbers)`);
        } else {
          maxPickupDimensions = Math.max(maxPickupDimensions, job.pickup.length);
          hasPickups = true;
        }
      }
      
      // Skills validation (optional but must be array if present)
      if (job.skills && (!Array.isArray(job.skills) || job.skills.some(skill => isNaN(skill) || skill <= 0))) {
        errors.push(`Job ${job.id}: Invalid skills array (must be positive integers)`);
      }
      
      // Time windows validation (optional but must be valid if present)
      if (job.time_windows) {
        if (!Array.isArray(job.time_windows)) {
          errors.push(`Job ${job.id}: Invalid time windows format`);
        } else {
          job.time_windows.forEach((tw, twIndex) => {
            if (!Array.isArray(tw) || tw.length !== 2 || tw[0] >= tw[1]) {
              errors.push(`Job ${job.id}: Invalid time window ${twIndex} (must be [start, end] with start < end)`);
            }
          });
        }
      }
    });
    
    // 4. Cross-validation checks
    // Capacity dimensions consistency
    if (maxCapacityDimensions > 0 && hasDeliveries && maxDeliveryDimensions !== maxCapacityDimensions) {
      errors.push(`Capacity dimensions mismatch: vehicles have ${maxCapacityDimensions} dimensions, but deliveries have ${maxDeliveryDimensions} dimensions`);
    }
    
    if (maxCapacityDimensions > 0 && hasPickups && maxPickupDimensions !== maxCapacityDimensions) {
      errors.push(`Capacity dimensions mismatch: vehicles have ${maxCapacityDimensions} dimensions, but pickups have ${maxPickupDimensions} dimensions`);
    }
    
    // Pickup/delivery dimensions consistency (CRITICAL - this was causing the error!)
    if (hasPickups && hasDeliveries && maxPickupDimensions !== maxDeliveryDimensions) {
      errors.push(`CRITICAL: Inconsistent pickup/delivery dimensions - pickups have ${maxPickupDimensions} dimensions, deliveries have ${maxDeliveryDimensions} dimensions. All must be the same!`);
    }
    
    // Skills overlap validation
    const vehicleSkills = new Set((request.vehicles || []).flatMap(v => v.skills || []));
    const jobSkills = new Set((request.jobs || []).flatMap(j => j.skills || []));
    const skillsOverlap = [...jobSkills].filter(skill => vehicleSkills.has(skill));
    
    if (jobSkills.size > 0 && skillsOverlap.length === 0) {
      errors.push(`Skills mismatch: jobs require skills [${[...jobSkills].join(',')}] but vehicles only have [${[...vehicleSkills].join(',')}]`);
    }
    
    // Capacity vs demand validation
    const totalCapacity = (request.vehicles || []).reduce((sum, v) => sum + (v.capacity?.[0] || 0), 0);
    const totalDemand = (request.jobs || []).reduce((sum, j) => sum + (j.delivery?.[0] || 0), 0);
    
    if (totalDemand > totalCapacity) {
      errors.push(`Capacity overflow: total demand (${totalDemand}) exceeds total vehicle capacity (${totalCapacity})`);
    }
    
    // Log validation results
    if (errors.length === 0) {
      addLog('✅ Pre-optimization validation passed', 'success');
      addLog(`📊 Summary: ${request.vehicles.length} vehicles, ${request.jobs.length} jobs, capacity=${totalCapacity}, demand=${totalDemand}`, 'info');
    } else {
      addLog(`❌ Pre-optimization validation failed with ${errors.length} errors:`, 'error');
      errors.forEach(error => addLog(`  • ${error}`, 'error'));
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }, [addLog]);

  // Call VROOM API
  const callVroomApi = useCallback(async (request: VroomRequest): Promise<VroomResponse> => {
    addLog('Sending request to optimisation engine via FastAPI...', 'info');
    
    try {
      const { runDirectOptimization } = await import('../services/api');
      const apiClient = (await import('../services/api')).default;
      
      // Transform request to match API expectations with proper types
      const apiRequest = {
        vehicles: (request.vehicles || []).map(v => ({
          id: Number(v.id),
          start: (v.start || []).map(coord => Number(coord)),
          end: (v.end || []).map(coord => Number(coord)),
          capacity: (v.capacity || []).map(cap => Number(cap)),
          skills: (v.skills || []).map(skill => Number(skill)),
          time_window: (v.time_window || []).map(time => Number(time)),
          costs: {
            fixed: Number(v.costs.fixed),
            per_hour: Number(v.costs.per_hour),
            per_km: Number(v.costs.per_km),
          },
          max_distance: v.max_distance ? Number(v.max_distance) : undefined,
          max_travel_time: v.max_travel_time ? Number(v.max_travel_time) : undefined,
        })),
        jobs: (request.jobs || []).map(j => ({
          id: Number(j.id),
          location: (j.location || []).map(coord => Number(coord)),
          service: Number(j.service),
          delivery: j.delivery && Array.isArray(j.delivery) ? j.delivery.map(d => Number(d)) : undefined,
          pickup: j.pickup && Array.isArray(j.pickup) ? j.pickup.map(p => Number(p)) : undefined,
          skills: (j.skills || []).map(skill => Number(skill)),
          priority: Number(j.priority),
          time_windows: j.time_windows && Array.isArray(j.time_windows) ? 
            j.time_windows.map(tw => (tw || []).map(time => Number(time))) : undefined,
        })),
        options: {
          g: Boolean(request.options.g),
          useMatrix: Boolean(request.options.useMatrix),
          threads: request.options.threads ? Number(request.options.threads) : 4,
        },
        // Context integration for optimization tracking
        scenario_id: currentScenario?.id || null,
        dataset_id: currentDataset?.id || null,
        dataset_name: currentDataset?.name || `optimization_${Date.now()}`,
        dataset_version: currentDataset?.version || '1.0.0',
        project_id: currentProject?.id || null,
      };

      // Log the request data for debugging
      console.log('🔍 [Optimization] Sending API request:', {
        vehicles: apiRequest.vehicles.length,
        jobs: apiRequest.jobs.length,
        sampleVehicle: apiRequest.vehicles[0],
        sampleJob: apiRequest.jobs[0],
        options: apiRequest.options,
        scenario_context: {
          scenario_id: apiRequest.scenario_id,
          dataset_name: apiRequest.dataset_name,
          dataset_version: apiRequest.dataset_version,
          project_id: apiRequest.project_id
        }
      });

      addLog(`📋 Context: scenario=${currentScenario?.name || 'none'} dataset=${currentDataset?.name || 'none'} project=${currentProject?.name || 'none'}`, 'info');
      addLog('Calling optimisation engine endpoint...', 'info');
      const result = await apiClient.runDirectOptimization(apiRequest);
      
      // Log the full VROOM response for debugging
      console.log('🔍 VROOM Response:', result);
      
      if (result.code !== 0) {
        addLog(`❌ Optimisation engine error (code ${result.code}): ${result.error || 'Unknown error'}`, 'error');
        console.log('❌ Full VROOM Error Response:', result);
        throw new Error(`Optimisation engine error (code ${result.code}): ${result.error || 'Unknown error'}`)
      }

      addLog('Optimisation completed successfully!', 'success');
      addLog(`📊 Generated ${(result.routes || []).length} routes with ${(result.unassigned || []).length} unassigned jobs`, 'info');
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      addLog(`Optimisation engine API error: ${errorMessage}`, 'error');
      throw error;
    }
  }, [addLog]);

  // Run optimization with context validation
  const runOptimization = useCallback(async () => {
    // Context validation
    if (!currentScenario) {
      addLog('Cannot optimize: No scenario selected', 'error');
      return;
    }
    
    if (!currentDataset) {
      addLog('Cannot optimize: No dataset selected', 'error');
      return;
    }
    
    if (!vehicles || !jobs || vehicles.length === 0 || jobs.length === 0) {
      addLog('Cannot optimize: No vehicles or jobs available in current dataset', 'error');
      return;
    }
    
    // Validate dataset completeness
    addLog(`📊 Validating dataset: ${currentDataset.name}`, 'info');
    const validation = await validateDataset(currentDataset.id);
    if (validation && !validation.is_valid) {
      addLog(`❌ Dataset validation failed: ${(validation.errors || []).map(e => e.message).join(', ')}`, 'error');
      return;
    }

    setOptimizationState('running');
    setCurrentStep(0);
    setProgress(0);
    setError(null);
    setResults(null);
    setLogs([]);

    try {
      // Step 1: Transform data
      setCurrentStep(0);
      setProgress(10);
      addLog('Transforming data to optimisation engine format...', 'info');
      const vroomRequest = transformToVroomFormat();
      await new Promise(resolve => setTimeout(resolve, 500));

      // Step 2: Validate data
      setCurrentStep(1);
      setProgress(30);
      addLog('Validating optimisation request data...', 'info');
      const validation = validateOptimizationData(vroomRequest);
      
      if (!validation.isValid) {
        throw new Error(`Validation failed: ${validation.errors.join('; ')}`);
      }
      
      await new Promise(resolve => setTimeout(resolve, 500));

      // Step 3: Send to optimisation engine
      setCurrentStep(2);
      setProgress(50);
      const result = await callVroomApi(vroomRequest);
      
      // Step 4: Process results
      setCurrentStep(3);
      setProgress(80);
      addLog('Processing optimization results...', 'info');
      await new Promise(resolve => setTimeout(resolve, 500));

      // Step 4: Store results with full context
      setCurrentStep(3);
      setProgress(90);
      addLog('Storing optimization results with dataset context...', 'info');
      
      try {
        const apiClient = (await import('../services/api')).default;
        await apiClient.storeOptimizationResults({
          project_id: currentProject!.id,
          scenario_id: currentScenario!.id,
          dataset_id: currentDataset!.id,
          dataset_name: currentDataset!.name,
          dataset_version: currentDataset!.version || '1.0.0',
          optimization_data: result,
          metadata: {
            algorithm: settings.algorithm,
            duration_ms: Date.now() - Date.now(), // TODO: Calculate actual duration
            settings: settings
          }
        });
        addLog('✅ Optimization results stored successfully', 'success');
      } catch (storageError) {
        addLog(`⚠️ Failed to store results: ${storageError instanceof Error ? storageError.message : 'Unknown error'}`, 'warning');
        // Continue even if storage fails - user still gets results
      }

      // Step 5: Complete
      setCurrentStep(4);
      setProgress(100);
      setResults(result);
      setOptimizationState('completed');
      addLog(`Optimization completed! Generated ${(result.routes || []).length} routes with ${(result.unassigned || []).length} unassigned jobs`, 'success');

    } catch (error) {
      setOptimizationState('error');
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setError(errorMessage);
      addLog(`Optimization failed: ${errorMessage}`, 'error');
    }
  }, [vehicles, jobs, currentScenario, currentDataset, validateDataset, transformToVroomFormat, callVroomApi, addLog]);

  // Stop optimization
  const stopOptimization = useCallback(() => {
    setOptimizationState('idle');
    setCurrentStep(0);
    setProgress(0);
    addLog('Optimization stopped by user', 'warning');
  }, [addLog]);

  // Reset optimization
  const resetOptimization = useCallback(() => {
    setOptimizationState('idle');
    setCurrentStep(0);
    setProgress(0);
    setResults(null);
    setError(null);
    setLogs([]);
  }, []);

  // Check VROOM health
  const checkVroomHealth = useCallback(async () => {
    setHealthStatus('checking');
    addLog('Checking optimisation engine health...', 'info');
    
    try {
      const apiClient = (await import('../services/api')).default;
      const healthResult = await apiClient.checkVROOMHealth();
      
      if (healthResult.vroom_available) {
        setHealthStatus('healthy');
        addLog(`Optimisation engine is healthy at ${healthResult.solver_url}`, 'success');
      } else {
        setHealthStatus('unhealthy');
        addLog(`Optimisation engine is not available: ${healthResult.status}`, 'error');
      }
    } catch (error) {
      setHealthStatus('unhealthy');
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      addLog(`Optimisation engine health check failed: ${errorMessage}`, 'error');
    }
  }, [addLog]);

  // Optimization steps
  const steps = [
    { label: 'Transform Data', description: 'Converting to optimisation engine format' },
    { label: 'Validate Data', description: 'Checking optimisation request validity' },
    { label: 'Solve Optimization', description: 'Running optimisation engine' },
    { label: 'Process Results', description: 'Analyzing optimization results' },
    { label: 'Complete', description: 'Optimization finished' },
  ];

  // Format time for display
  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  // Format distance for display
  const formatDistance = (meters: number): string => {
    if (meters >= 1000) {
      return `${(meters / 1000).toFixed(1)} km`;
    }
    return `${meters} m`;
  };

  // Test optimisation engine
  const testOptimisationEngine = useCallback(async () => {
    addLog('Running optimisation engine test optimization...', 'info');
    
    try {
      const apiClient = (await import('../services/api')).default;
      const testResult = await apiClient.testVROOMOptimization();
      
      if (testResult.success) {
        addLog('Optimisation engine test completed successfully!', 'success');
        addLog(`Generated ${testResult.test_summary.routes_generated} routes for ${testResult.test_summary.vehicles} vehicles and ${testResult.test_summary.jobs} jobs`, 'info');
        if (testResult.test_summary.total_distance > 0) {
          addLog(`Total distance: ${formatDistance(testResult.test_summary.total_distance)}`, 'info');
          addLog(`Total duration: ${formatTime(testResult.test_summary.total_duration)}`, 'info');
        }
      } else {
        addLog('Optimisation engine test failed', 'error');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      addLog(`Optimisation engine test failed: ${errorMessage}`, 'error');
    }
  }, [addLog]);


  if (!currentProject) {
    return (
      <ProfessionalLayout
        pageTitle="Optimization"
        pageSubtitle="Please select a project to run optimization"
        activeTab="Optimization"
        actionButtons={
          <Button 
            variant="contained"
            onClick={() => navigate('/projects')}
            startIcon={<ArrowBackIcon />}
          >
            Select Project
          </Button>
        }
      >
        <Alert severity="warning">
          Please select a project to run optimization.
        </Alert>
      </ProfessionalLayout>
    );
  }

  return (
    <ProfessionalLayout
      pageTitle="Route Optimization"
      pageSubtitle={`Optimisation engine-powered vehicle routing optimization for ${currentProject.name}`}
      activeTab="Optimization"
      actionButtons={
        <>
          <Button
            variant="outlined"
            startIcon={<Assessment />}
            onClick={checkVroomHealth}
            disabled={healthStatus === 'checking'}
            color={
              healthStatus === 'healthy' ? 'success' :
              healthStatus === 'unhealthy' ? 'error' : 'primary'
            }
            sx={{ mr: 1 }}
          >
            Health Check
          </Button>
          <Button
            variant="outlined"
            startIcon={<Settings />}
            onClick={() => setSettingsOpen(true)}
            sx={{ mr: 1 }}
          >
            Settings
          </Button>
          <Button
            variant="contained"
            startIcon={optimizationState === 'running' ? <CircularProgress size={20} /> : <PlayArrow />}
            onClick={runOptimization}
            disabled={optimizationState === 'running' || isLoading || !vehicles || !jobs || vehicles.length === 0 || jobs.length === 0 || !currentScenario || !currentDataset}
          >
            {optimizationState === 'running' ? 'Optimizing...' : 'Run Optimization'}
          </Button>
        </>
      }
    >
      {/* Context Breadcrumbs */}
      <Box sx={{ mb: 3 }}>
        <ScenarioDatasetBreadcrumbs />
      </Box>
      
      {/* Context Status Alert */}
      {(!currentScenario || !currentDataset) && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="subtitle2">Incomplete Context</Typography>
          <Typography variant="body2">
            {!currentScenario && !currentDataset 
              ? 'Please select a scenario and dataset before running optimization.'
              : !currentScenario 
              ? 'Please select a scenario to proceed with optimization.'
              : 'Please select a dataset to load data for optimization.'}
          </Typography>
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            Navigate to: Project → Scenarios → Data Management → Optimization
          </Typography>
        </Alert>
      )}
      
      {/* Dataset Loading Error */}
      {dataError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="subtitle2">Dataset Loading Error</Typography>
          <Typography variant="body2">
            Failed to load dataset-specific data. Please refresh or check your dataset selection.
          </Typography>
          <Button 
            size="small" 
            onClick={refetchDatasetData} 
            sx={{ mt: 1 }}
            variant="outlined"
          >
            Retry Loading
          </Button>
        </Alert>
      )}

      {/* Dataset Context Information */}
      {currentScenario && currentDataset && (
        <Card sx={{ mb: 3, backgroundColor: theme.palette.primary.light + '20' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip 
                  label={`Scenario: ${currentScenario.name}`} 
                  color="primary" 
                  size="small" 
                />
                <Chip 
                  label={`Dataset: ${currentDataset.name}`} 
                  color="secondary" 
                  size="small" 
                />
              </Box>
              <Box sx={{ flexGrow: 1 }} />
              {(statistics || dataValidation) && (
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    Vehicles: {dataValidation.vehicleCount || statistics?.vehicle_count || 0} | 
                    Jobs: {dataValidation.jobCount || statistics?.job_count || 0} | 
                    Locations: {dataValidation.locationCount || statistics?.location_count || 0}
                  </Typography>
                  {!isDataComplete && (
                    <Chip 
                      label={`Missing: ${dataValidation.missingData.join(', ')}`}
                      color="warning" 
                      size="small" 
                      variant="outlined"
                    />
                  )}
                  {isDataComplete && (
                    <Chip 
                      label="Data Complete"
                      color="success" 
                      size="small" 
                      variant="outlined"
                    />
                  )}
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      <Grid container spacing={3}>
        {/* Left Column - Controls */}
        <Grid size={{ xs: 12, lg: 4 }}>
          {/* Data Summary */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Optimization Data
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 4 }}>
                  <Box textAlign="center">
                    <DirectionsCar color="primary" sx={{ fontSize: 32, mb: 1 }} />
                    <Typography variant="h5" fontWeight="bold">
                      {isLoading ? '...' : (vehicles || []).length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Vehicles
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <Box textAlign="center">
                    <Work color="primary" sx={{ fontSize: 32, mb: 1 }} />
                    <Typography variant="h5" fontWeight="bold">
                      {isLoading ? '...' : (jobs || []).length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Jobs
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <Box textAlign="center">
                    <LocationOn color="primary" sx={{ fontSize: 32, mb: 1 }} />
                    <Typography variant="h5" fontWeight="bold">
                      {isLoading ? '...' : (locations || []).length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Locations
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Optimization Controls */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Optimization Controls
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  startIcon={optimizationState === 'running' ? <CircularProgress size={20} /> : <PlayArrow />}
                  onClick={runOptimization}
                  disabled={optimizationState === 'running' || isLoading || !vehicles || !jobs || vehicles.length === 0 || jobs.length === 0 || !currentScenario || !currentDataset}
                  sx={{ mb: 1 }}
                >
                  {optimizationState === 'running' ? 'Optimizing...' : 'Run Optimization'}
                </Button>
                
                {optimizationState === 'running' && (
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<Stop />}
                    onClick={stopOptimization}
                    color="error"
                    sx={{ mb: 1 }}
                  >
                    Stop Optimization
                  </Button>
                )}
                
                {(optimizationState === 'completed' || optimizationState === 'error') && (
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<Refresh />}
                    onClick={resetOptimization}
                  >
                    Reset
                  </Button>
                )}
              </Box>

              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<BugReport />}
                  onClick={runDataDiagnostics}
                  color="info"
                >
                  Data Diagnostics
                </Button>
                
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={healthStatus === 'checking' ? <CircularProgress size={16} /> : <Assessment />}
                  onClick={checkVroomHealth}
                  disabled={healthStatus === 'checking'}
                  color={
                    healthStatus === 'healthy' ? 'success' :
                    healthStatus === 'unhealthy' ? 'error' : 'primary'
                  }
                  sx={{ flex: 1 }}
                >
                  Health Check
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PlayArrow />}
                  onClick={testOptimisationEngine}
                  disabled={optimizationState === 'running'}
                  sx={{ flex: 1 }}
                >
                  Test Opt.
                </Button>
              </Box>

              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Assessment />}
                  onClick={runDataDiagnostics}
                  disabled={isLoading}
                  sx={{ flex: 1 }}
                >
                  Data Diagnostics
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Refresh />}
                  onClick={refetchDatasetData}
                  disabled={isLoading}
                  color="info"
                  sx={{ flex: 1 }}
                >
                  Refresh Data
                </Button>
              </Box>

              <Button
                variant="text"
                fullWidth
                startIcon={<Settings />}
                onClick={() => setSettingsOpen(true)}
              >
                Optimization Settings
              </Button>
            </CardContent>
          </Card>

          {/* VROOM Status */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Optimisation Engine Status
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Chip
                  label={
                    healthStatus === 'unknown' ? 'UNKNOWN' :
                    healthStatus === 'checking' ? 'CHECKING...' :
                    healthStatus === 'healthy' ? 'HEALTHY' : 'UNHEALTHY'
                  }
                  color={
                    healthStatus === 'healthy' ? 'success' :
                    healthStatus === 'unhealthy' ? 'error' :
                    healthStatus === 'checking' ? 'primary' : 'default'
                  }
                  icon={
                    healthStatus === 'healthy' ? <CheckCircle /> :
                    healthStatus === 'unhealthy' ? <ErrorIcon /> :
                    healthStatus === 'checking' ? <CircularProgress size={16} /> : <Warning />
                  }
                  size="small"
                />
                <Typography variant="caption" color="text.secondary">
                  {healthStatus === 'healthy' ? 'Optimisation engine is ready for optimisation' :
                   healthStatus === 'unhealthy' ? 'Optimisation engine is not available' :
                   healthStatus === 'checking' ? 'Checking engine availability...' :
                   'Click Health Check to verify optimisation engine status'}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Optimization Status */}
          {optimizationState !== 'idle' && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Optimization Status
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Chip
                    label={optimizationState.toUpperCase()}
                    color={
                      optimizationState === 'completed' ? 'success' :
                      optimizationState === 'error' ? 'error' :
                      optimizationState === 'running' ? 'primary' : 'default'
                    }
                    icon={
                      optimizationState === 'completed' ? <CheckCircle /> :
                      optimizationState === 'error' ? <ErrorIcon /> :
                      optimizationState === 'running' ? <CircularProgress size={16} /> : undefined
                    }
                  />
                </Box>

                {optimizationState === 'running' && (
                  <Box sx={{ mb: 2 }}>
                    <LinearProgress variant="determinate" value={progress} />
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      {progress}% Complete
                    </Typography>
                  </Box>
                )}

                <Stepper activeStep={currentStep} orientation="vertical">
                  {steps.map((step, index) => (
                    <Step key={index}>
                      <StepLabel>{step.label}</StepLabel>
                      <StepContent>
                        <Typography variant="body2" color="text.secondary">
                          {step.description}
                        </Typography>
                      </StepContent>
                    </Step>
                  ))}
                </Stepper>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Right Column - Results */}
        <Grid size={{ xs: 12, lg: 8 }}>
          {/* Results Summary */}
          {results && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Optimization Results
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid size={{ xs: 6, md: 3 }}>
                    <Box textAlign="center">
                      <Typography variant="h4" fontWeight="bold" color="primary.main">
                        {results.summary.routes}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Routes Generated
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 6, md: 3 }}>
                    <Box textAlign="center">
                      <Typography variant="h4" fontWeight="bold" color="success.main">
                        {formatDistance(results.summary.distance)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total Distance
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 6, md: 3 }}>
                    <Box textAlign="center">
                      <Typography variant="h4" fontWeight="bold" color="info.main">
                        {formatTime(results.summary.duration)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total Time
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 6, md: 3 }}>
                    <Box textAlign="center">
                      <Typography variant="h4" fontWeight="bold" color={results.summary.unassigned > 0 ? "warning.main" : "success.main"}>
                        {results.summary.unassigned}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Unassigned Jobs
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Routes Details */}
          {results && Array.isArray(results.routes) && results.routes.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Route Details
                </Typography>
                
                {(Array.isArray(results.routes) ? results.routes : []).map((route, index) => (
                  <Accordion key={index}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <DirectionsCar sx={{ mr: 2 }} />
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            Vehicle {route.vehicle} - {(Array.isArray(route.steps) ? route.steps.length : 0) - 2} stops
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDistance(route.distance)} • {formatTime(route.duration)} • Cost: {route.cost}
                          </Typography>
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <List dense>
                        {(Array.isArray(route.steps) ? route.steps : []).map((step, stepIndex) => (
                          <ListItem key={stepIndex}>
                            <ListItemIcon>
                              {step.type === 'start' ? <DirectionsCar /> :
                               step.type === 'job' ? <Work /> : <LocationOn />}
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                step.type === 'start' ? 'Start Depot' :
                                step.type === 'end' ? 'End Depot' :
                                `Job ${step.id}`
                              }
                              secondary={
                                `Arrival: ${formatTime(step.arrival)} • Distance: ${formatDistance(step.distance)}` +
                                (step.service ? ` • Service: ${formatTime(step.service)}` : '')
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Unassigned Jobs */}
          {results && Array.isArray(results.unassigned) && results.unassigned.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom color="warning.main">
                  Unassigned Jobs ({results.unassigned.length})
                </Typography>
                
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Common Solutions:</Typography>
                  <Typography variant="body2" component="div">
                    • Check vehicle capacities vs job delivery requirements<br/>
                    • Verify skill compatibility between vehicles and jobs<br/>
                    • Ensure valid coordinates for all job locations<br/>
                    • Review time window constraints<br/>
                    • Check vehicle distance/travel time limits
                  </Typography>
                </Alert>
                
                <List>
                  {(Array.isArray(results.unassigned) ? results.unassigned : []).map((unassigned, index) => {
                    const job = jobs && Array.isArray(jobs) ? jobs.find(j => j.id === unassigned.id) : null;
                    const jobLocation = job && locations && Array.isArray(locations) ? locations.find(loc => loc.id === job.location_id) : null;
                    
                    return (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <Warning color="warning" />
                        </ListItemIcon>
                        <ListItemText
                          primary={`Job ${unassigned.id}${job ? ` - ${job.description}` : ''}`}
                          secondary={
                            <Box>
                              <Typography variant="body2" color="error">
                                Reason: {unassigned.reason}
                              </Typography>
                              {job && (
                                <Typography variant="caption" display="block">
                                  Location: {jobLocation ? jobLocation.name : `ID ${job.location_id} (not found)`} • 
                                  Delivery: {JSON.stringify(job.delivery)} • 
                                  Skills: {JSON.stringify(job.skills)} • 
                                  Service: {formatDuration(job.service)}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </CardContent>
            </Card>
          )}

          {/* Logs */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Optimization Logs
              </Typography>
              
              <Paper variant="outlined" sx={{ height: 300, overflow: 'auto', p: 2, backgroundColor: theme.palette.grey[50] }}>
                {logs.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No logs available. Run optimization to see logs.
                  </Typography>
                ) : (
                  (Array.isArray(logs) ? logs : []).map((log, index) => (
                    <Box key={index} sx={{ mb: 1, fontFamily: 'monospace', fontSize: '12px' }}>
                      <Box component="span" sx={{ 
                        color: 
                          log.type === 'error' ? theme.palette.error.main :
                          log.type === 'warning' ? theme.palette.warning.main :
                          log.type === 'success' ? theme.palette.success.main :
                          theme.palette.text.secondary,
                        mr: 1 
                      }}>
                        [{log.timestamp}]
                      </Box>
                      <Box component="span" sx={{ 
                        color:
                          log.type === 'error' ? theme.palette.error.main :
                          log.type === 'warning' ? theme.palette.warning.main :
                          log.type === 'success' ? theme.palette.success.main :
                          theme.palette.text.primary
                      }}>
                        {log.message}
                      </Box>
                    </Box>
                  ))
                )}
              </Paper>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Optimization History Section */}
      <Card sx={{ mt: 4 }}>
        <CardContent>
          <OptimizationHistory 
            onSelectResult={(result) => {
              console.log('Selected optimization result:', result.id);
              // TODO: Load and display selected result
            }}
            showCompare={true}
          />
        </CardContent>
      </Card>

      {/* API Diagnostics Section */}
      <OptimizationDebugger projectId={currentProject.id} />

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Optimization Settings</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Algorithm</InputLabel>
              <Select
                value={settings.algorithm}
                label="Algorithm"
                onChange={(e: SelectChangeEvent<string>) => setSettings(prev => ({ ...prev, algorithm: e.target.value as 'vroom' | 'genetic' | 'simulated_annealing' }))}
              >
                <MenuItem value="vroom">Optimisation engine (Recommended)</MenuItem>
                <MenuItem value="genetic">Genetic Algorithm</MenuItem>
                <MenuItem value="simulated_annealing">Simulated Annealing</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={settings.includeGeometry}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSettings(prev => ({ ...prev, includeGeometry: e.target.checked }))}
                />
              }
              label="Include Route Geometry"
              sx={{ mb: 2 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={settings.useMatrix}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSettings(prev => ({ ...prev, useMatrix: e.target.checked }))}
                />
              }
              label="Use Distance Matrix"
              sx={{ mb: 3 }}
            />

            <TextField
              fullWidth
              label="Thread Count"
              type="number"
              value={settings.threads}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSettings(prev => ({ ...prev, threads: parseInt(e.target.value) || 4 }))}
              sx={{ mb: 2 }}
              inputProps={{ min: 1, max: 8 }}
            />

            <TextField
              fullWidth
              label="Max Distance (meters)"
              type="number"
              value={settings.maxDistance}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSettings(prev => ({ ...prev, maxDistance: parseInt(e.target.value) || 100000 }))}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Max Travel Time (seconds)"
              type="number"
              value={settings.maxTravelTime}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSettings(prev => ({ ...prev, maxTravelTime: parseInt(e.target.value) || 28800 }))}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Time Window Tolerance (seconds)"
              type="number"
              value={settings.timeWindowTolerance}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSettings(prev => ({ ...prev, timeWindowTolerance: parseInt(e.target.value) || 300 }))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)}>Cancel</Button>
          <Button onClick={() => setSettingsOpen(false)} variant="contained">Save Settings</Button>
        </DialogActions>
      </Dialog>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mt: 3 }}>
          <Typography variant="subtitle2">Optimization Error</Typography>
          {error}
        </Alert>
      )}
    </ProfessionalLayout>
  );
};
